import {asyncNoop, sleep} from '@benchling/js-utils';

import AutosaveManager from 'procedures/redux/autosaveManager';
import {procedureFlowchartReleased} from 'procedures/redux/proceduresActions';
import {getStore} from 'redux-stores/Store';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

import proceduresAutosaveManagerCallbacks from './proceduresAutosaveManagerCallbacks';

const PROCEDURES_AUTOSAVE_MANAGER_FRIENDLY_NAME = 'Procedure';
/**
 * This hook is a thin wrapper around the AutosaveManager, customized for procedures. It needs to be
 * used in a component that owns the `save` API call.
 *
 *   ~~ It is only useful for legacy procedures. For PoW procedures, this is a noop ~~
 *
 * Its main role is to manage the lifecycle of the AutosaveManager. Not only does it do the normal
 * initialize/destroy, but this hook also manages queuing. E.g., if the user switches to a different
 * tab, then switches back /before the save has completed/, we report that information so downstream
 * components can respond appropriately. (See ProcedureAutosaveManager for more.)
 *
 * Additional Redux-management tools are provided by ProcedureFlowchartStateManager.
 *
 * Note that callers must install a save handler directly via AutosaveManager.updateSaveHandler.
 */
export default function useLegacyProceduresAutosaveManager(itemId: string, isPoW: boolean): boolean {
  // PoW procedures start out in a state of readiness (for this hook, at least)
  const [hasManagerBeenCreated, setHasManagerBeenCreated] = useStateIfMounted(isPoW);

  useOnMount(() => {
    if (isPoW) {
      return;
    }

    async function setup(): Promise<void> {
      // We don't have a `doSave` function yet, as this hook is designed to block rendering of the components
      // that can build `doSave`
      await AutosaveManager.initialize(itemId, PROCEDURES_AUTOSAVE_MANAGER_FRIENDLY_NAME, {
        ...proceduresAutosaveManagerCallbacks,
        doSave: asyncNoop,
      });
      setHasManagerBeenCreated(true);
    }
    setup();

    return () => {
      // If the page is unmounted while the procedure is dirty, we do a silent "save immediately." Doing so
      // eliminates the need for a "leave before saving?" warning.
      async function cleanup(): Promise<void> {
        // While this sleep isn't strictly necessary, it gives the unmounting page a chance to refresh the
        // save handler installed into the AutosaveManager with empty callbacks. (The callbacks are for
        // bookkeeping, which don't affect the save itself.)
        await sleep(0);
        const isDirty = proceduresAutosaveManagerCallbacks.isDirty(itemId);
        if (isDirty) {
          await AutosaveManager.saveImmediately(itemId, () => ({}), AutosaveManager.NO_OVERLAY, false);
        }
        getStore().dispatch(procedureFlowchartReleased(itemId));
        AutosaveManager.destroy(itemId);
      }
      cleanup();
    };
  });

  return hasManagerBeenCreated;
}
